import { Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import { isNativePlatform } from '../lib/storage.js';
import { useCatalogConfig } from '../hooks/useCatalog.js';
import { isCustomerApp, homePathForRole } from '../lib/appTarget.js';
import { PageSpinner } from './Spinner.jsx';

/**
 * What "/" shows.
 *
 * In the native app a returning, already-signed-in user shouldn't be dropped on
 * the marketing landing page — they want their next delivery and today's menu.
 * So on native we send an authenticated user straight to their home: customers
 * to the members dashboard (upcoming delivery + menu), drivers to their route,
 * admins to the dashboard. On the web the landing page stays the front door for
 * everyone, signed in or not.
 *
 * `loading` is waited out rather than treated as signed-out, so a cold start
 * doesn't flash the landing page before the session is confirmed. The landing
 * element is passed in as children so it stays lazy-loaded from App.jsx.
 */
export default function HomeRoute({ children }) {
  const { user, loading } = useAuth();
  const configQuery = useCatalogConfig();

  // The driver and admin apps are role tools: on the web they still show the
  // landing page, but always route a build to sign-in → its dashboard.
  if (!isCustomerApp) {
    if (loading) return <PageSpinner label="Loading" />;
    if (user) return <Navigate to={homePathForRole(user.role)} replace />;
    return <Navigate to="/login" replace />;
  }

  // Customer app: the redirect only applies inside the native app; the website
  // always shows Landing.
  if (!isNativePlatform()) return children;

  if (loading) return <PageSpinner label="Loading your box" />;

  if (user) return <Navigate to={homePathForRole(user.role)} replace />;

  // Not signed in, in the app → Zepto-style mobile onboarding is the front door,
  // but only when the backend actually has mobile-OTP turned on. Otherwise fall
  // back to the marketing landing so the app is never a dead end.
  if (configQuery.isLoading) return <PageSpinner label="Loading your box" />;
  if (configQuery.data?.phoneOtpEnabled) return <Navigate to="/onboard" replace />;
  return children;
}
